import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Chip,
    Tooltip,
    Alert,
    Collapse,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Menu,
    ListItemIcon,
    ListItemText,
    TablePagination,
    TableSortLabel,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { csCZ } from '@mui/x-data-grid/locales';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LockReset as ResetPasswordIcon,
    ToggleOn as ToggleIcon,
    Pin as PinIcon,
    CheckCircle as ActivateIcon,
    Cancel as DeactivateIcon,
    SendToMobile as TestNotificationIcon,
    NotificationsActive as NotificationsActiveIcon,
    NotificationsOff as NotificationsOffIcon,
    PhoneAndroid as PhoneAndroidIcon,
    RestartAlt as ResetTestLimitIcon,
    History as HistoryIcon,
    Assignment as AssignmentIcon,
    ExpandMore as ExpandMoreIcon,
    MoreVert as MoreVertIcon,
    MailOutline as InviteIcon,
    FileDownload as FileDownloadIcon,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { PinRevealCell } from '../../components/admin/PinRevealCell';
import {
    useAllSpecializaceQuery,
    useBulkAssignSpecializaceMutation,
} from '../../api/JsdhNotifikaceService/service/SpecializaceMutations';
import {
    useKontrolyForUserQuery,
    useCreateKontrolaMutation,
} from '../../api/JsdhNotifikaceService/service/KontrolyMutations';
import type { KontrolaStatusResponse, CreateKontrolaRequest } from '../../api/JsdhNotifikaceService/generated/models/Kontrola';
import type { MetadataFieldSchema } from '../../api/JsdhNotifikaceService/generated/models/TypKontroly';
import { useActiveTechnikaQuery } from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import {
    useAllUsersQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useResetUserPasswordMutation,
    useToggleUserStatusMutation,
    useSetUserPinMutation,
    useDeleteUserPinMutation,
    useBulkSetPinMutation,
    useBulkDeletePinMutation,
    useSendUserTestNotificationMutation,
    useResetUserTestLimitMutation,
    useUserTestHistoryQuery,
    useSendUserInvitationMutation,
} from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { Uzivatel, CreateUserRequest, UpdateUserRequest } from '../../api/JsdhNotifikaceService/generated';
import { useHzsSmenaAktualniQuery } from '../../api/JsdhNotifikaceService/service/DostupnostMutations';
import AdminService from '../../api/JsdhNotifikaceService/service/AdminService';
import { useToast } from '../../components/toast/useToast';
import { useAuth } from '../../context/AuthContext';
import { useDownloadHromadnyPosudekMutation } from '../../api/JsdhNotifikaceService/service/KontrolyDokumentMutations';
import { downloadFile } from '../../utils/downloadFile';
import PrevzatoPopisek from '../../components/admin/kontroly/PrevzatoPopisek';


const UsersPage: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAdmin } = useAuth();
    const { showToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { data: users, isLoading } = useAllUsersQuery();
    const { data: hzsSmenaData } = useHzsSmenaAktualniQuery();
    const currentShift = hzsSmenaData?.currentShift || null;
    const createMutation = useCreateUserMutation();
    const updateMutation = useUpdateUserMutation();
    const deleteMutation = useDeleteUserMutation();
    const resetPasswordMutation = useResetUserPasswordMutation();
    const toggleStatusMutation = useToggleUserStatusMutation();
    const setUserPinMutation = useSetUserPinMutation();
    const deleteUserPinMutation = useDeleteUserPinMutation();
    const testNotificationMutation = useSendUserTestNotificationMutation();
    const resetTestLimitMutation = useResetUserTestLimitMutation();
    const sendInvitationMutation = useSendUserInvitationMutation();
    const { data: allSpecializace } = useAllSpecializaceQuery();
    const bulkAssignSpecializaceMutation = useBulkAssignSpecializaceMutation();
    const bulkSetPinMutation = useBulkSetPinMutation();
    const bulkDeletePinMutation = useBulkDeletePinMutation();
    const hromadnyPosudekMutation = useDownloadHromadnyPosudekMutation();

    const [openDialog, setOpenDialog] = useState(false);
    const [showInviteConfirm, setShowInviteConfirm] = useState(false);
    const [invitingLoading, setInvitingLoading] = useState(false);
    const [openResetTestLimitDialog, setOpenResetTestLimitDialog] = useState(false);
    const [openTestHistoryDialog, setOpenTestHistoryDialog] = useState(false);
    const [testHistoryUserId, setTestHistoryUserId] = useState<number | null>(null);

    const { data: testHistoryData, isLoading: testHistoryLoading } = useUserTestHistoryQuery(testHistoryUserId);
    const [openTestNotificationDialog, setOpenTestNotificationDialog] = useState(false);
    const [testNotificationOptions, setTestNotificationOptions] = useState({ fcm: true });
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [openPinDialog, setOpenPinDialog] = useState(false);
    const [openGeneratedPinDialog, setOpenGeneratedPinDialog] = useState(false);
    const [generatedPin, setGeneratedPin] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<Uzivatel | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [newPin, setNewPin] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Bulk operations state
    const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
    const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
    const [openBulkStatusDialog, setOpenBulkStatusDialog] = useState(false);
    const [bulkStatusAction, setBulkStatusAction] = useState<'activate' | 'deactivate'>('activate');
    const [bulkOperationProgress, setBulkOperationProgress] = useState(false);
    const [openBulkSpecializaceDialog, setOpenBulkSpecializaceDialog] = useState(false);
    const [selectedBulkSpecializace, setSelectedBulkSpecializace] = useState('');
    const [openBulkSetPinDialog, setOpenBulkSetPinDialog] = useState(false);
    const [openBulkDeletePinDialog, setOpenBulkDeletePinDialog] = useState(false);
    const [bulkPin, setBulkPin] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [menuUser, setMenuUser] = useState<Uzivatel | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [orderBy, setOrderBy] = useState<string>('jmeno');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    const [formData, setFormData] = useState<CreateUserRequest>({
        jmeno: '',
        telefon: 0,
        email: '',
        funkce: [],
        specializace: [],
        aktivni: true,
        notifikaceEnabled: true,
        role: 'USER',
        generatePin: false,
        sendPasswordEmail: false,
    });
    const [formError, setFormError] = useState<string | null>(null);
    const [sendResetEmail, setSendResetEmail] = useState(false);

    const [showAdminAccess, setShowAdminAccess] = useState(false);
    const [showKontroly, setShowKontroly] = useState(false);
    const [kontrolaDialogOpen, setKontrolaDialogOpen] = useState(false);
    const [kontrolaTyp, setKontrolaTyp] = useState<KontrolaStatusResponse | null>(null);
    const [kontrolaDatum, setKontrolaDatum] = useState<Dayjs | null>(dayjs());
    const [kontrolaPoznamka, setKontrolaPoznamka] = useState('');
    const [kontrolaMetadata, setKontrolaMetadata] = useState<Record<string, any>>({});

    const { data: userKontroly } = useKontrolyForUserQuery(selectedUser?.id || null);
    const createKontrolaMutation = useCreateKontrolaMutation();
    const { data: aktivniTechnika } = useActiveTechnikaQuery();

    // Platné kódy funkcí
    const FUNKCE_OPTIONS = [
        { kod: 'VD', nazev: 'Velitel družstva' },
        { kod: 'S', nazev: 'Strojník' },
        { kod: 'H', nazev: 'Hasič' },
    ];

    const handleOpenCreate = () => {
        setSelectedUser(null);
        setFormData({
            jmeno: '',
            telefon: 0,
            email: '',
            funkce: [],
            specializace: [],
            aktivni: true,
            notifikaceEnabled: true,
            role: 'USER',
            generatePin: false,
        });
        setShowAdminAccess(false);
        setFormError(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (user: Uzivatel) => {
        setSelectedUser(user);
        // user.funkce může být pole objektů {id, kod, nazev} nebo pole stringů nebo string
        // Pomocná funkce pro bezpečné získání kódu funkce
        const getFunkceKod = (f: unknown): string | undefined => {
            if (typeof f === 'string') return f;
            if (f && typeof f === 'object' && 'kod' in f && typeof (f as { kod: unknown }).kod === 'string') {
                return (f as { kod: string }).kod;
            }
            return undefined;
        };
        const funkceArray = Array.isArray(user.funkce)
            ? user.funkce.map(getFunkceKod).filter((kod): kod is string => kod !== undefined)
            : user.funkce
                ? [getFunkceKod(user.funkce)].filter((kod): kod is string => kod !== undefined)
                : [];
        const getSpecializaceKod = (s: unknown): string | undefined => {
            if (typeof s === 'string') return s;
            if (s && typeof s === 'object' && 'kod' in s && typeof (s as { kod: unknown }).kod === 'string') {
                return (s as { kod: string }).kod;
            }
            return undefined;
        };
        const specializaceArray = Array.isArray(user.specializace)
            ? user.specializace.map(getSpecializaceKod).filter((kod): kod is string => kod !== undefined)
            : user.specializace
                ? [getSpecializaceKod(user.specializace)].filter((kod): kod is string => kod !== undefined)
                : [];
        setFormData({
            jmeno: user.jmeno,
            telefon: user.telefon,
            email: user.email || '',
            funkce: funkceArray,
            specializace: specializaceArray,
            aktivni: user.aktivni,
            notifikaceEnabled: user.notifikaceEnabled ?? true,
            role: user.role?.replace(/^ROLE_/, '') || 'USER',
        });
        setShowAdminAccess(!!(user.email));
        setFormError(null);
        setOpenDialog(true);
    };

    const handleOpenDelete = (user: Uzivatel) => {
        setSelectedUser(user);
        setOpenDeleteDialog(true);
    };

    const handleOpenResetPassword = (user: Uzivatel) => {
        setSelectedUser(user);
        setNewPassword('');
        setSendResetEmail(false);
        setOpenPasswordDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        setShowKontroly(false);
        setFormError(null);
    };

    const handleSubmit = () => {
        setFormError(null);
        if (selectedUser) {
            const updateData: UpdateUserRequest = {
                jmeno: formData.jmeno,
                telefon: formData.telefon,
                email: formData.email?.trim() ? formData.email : undefined,
                funkce: formData.funkce,
                specializace: formData.specializace,
                aktivni: formData.aktivni,
                notifikaceEnabled: formData.notifikaceEnabled,
                role: formData.role,
            };
            updateMutation.mutate(
                { id: selectedUser.id, data: updateData },
                {
                    onSuccess: handleCloseDialog,
                    onError: (err) => {
                        // Dialog ponecháme otevřený a zobrazíme inline chybu z BE
                        setFormError(err?.message || 'Nepodařilo se aktualizovat uživatele.');
                    },
                }
            );
        } else {
            const createData = {
                ...formData,
                email: formData.email || undefined,
            };
            createMutation.mutate(createData, {
                onSuccess: (createdUser) => {
                    handleCloseDialog();
                    // HO-113: PIN se vrací jednorázově v generatedPin
                    if (createdUser?.generatedPin) {
                        setGeneratedPin(createdUser.generatedPin);
                        setOpenGeneratedPinDialog(true);
                    }
                },
                onError: (err) => {
                    // Dialog ponecháme otevřený a zobrazíme inline chybu z BE
                    setFormError(err?.message || 'Nepodařilo se vytvořit uživatele.');
                },
            });
        }
    };

    const handleDelete = () => {
        if (selectedUser) {
            deleteMutation.mutate(selectedUser.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelectedUser(null);
                },
            });
        }
    };

    const handleResetPassword = () => {
        if (selectedUser && (newPassword || sendResetEmail)) {
            resetPasswordMutation.mutate(
                {
                    id: selectedUser.id,
                    newPassword: sendResetEmail ? undefined : newPassword,
                    sendPasswordEmail: sendResetEmail,
                },
                {
                    onSuccess: () => {
                        setOpenPasswordDialog(false);
                        setSelectedUser(null);
                        setNewPassword('');
                        setSendResetEmail(false);
                    },
                }
            );
        }
    };

    const handleSendInvitation = async () => {
        if (!selectedUser) return;
        setShowInviteConfirm(false);
        setInvitingLoading(true);

        // 1. Client validation
        if (!formData.jmeno?.trim() || !formData.telefon || !formData.email?.trim()) {
            showToast("Vyplňte jméno, telefon a email před odesláním pozvánky.", "error");
            setInvitingLoading(false);
            return;
        }

        // 2. PUT update via direct service call (bypass useUpdateUserMutation success toast).
        // Explicit UpdateUserRequest mapping - NOT raw formData (has create-only fields).
        const updatePayload: UpdateUserRequest = {
            jmeno: formData.jmeno,
            telefon: formData.telefon,
            email: formData.email,
            funkce: formData.funkce,
            specializace: formData.specializace,
            aktivni: formData.aktivni,
            notifikaceEnabled: formData.notifikaceEnabled,
            role: formData.role,
        };
        try {
            await AdminService.updateUser(selectedUser.id, updatePayload);
        } catch (error: any) {
            // Update fáze selhala — ukaž toast z handleru, dialog zůstává otevřený pro retry.
            setInvitingLoading(false);
            showToast(error?.message || "Aktualizace uživatele selhala.", "error");
            return;
        }

        // Invalidate cache after update so list reflects new data even if invitation step fails next.
        queryClient.invalidateQueries({ queryKey: ["adminUsers"] });

        // 3. POST send-invitation — onSuccess/onError mutace řeší toast samostatně,
        // handler tedy NESMÍ duplikovat toast (proto separátní try/catch jen pro update).
        try {
            await sendInvitationMutation.mutateAsync(selectedUser.id);
            // 4. Success: close dialog (mutation onSuccess handles invalidation + toast).
            setInvitingLoading(false);
            handleCloseDialog();
        } catch {
            // onError v mutaci ukázal toast. Dialog zůstává otevřený — admin klikne znovu = retry.
            setInvitingLoading(false);
        }
    };

    const handleSendInvitationFromMenu = (user: Uzivatel) => {
        if (!user.email?.trim()) {
            showToast("Uživatel nemá email - doplňte ho v editaci.", "error");
            return;
        }
        sendInvitationMutation.mutate(user.id);
    };

    const handleToggleStatus = (user: Uzivatel) => {
        toggleStatusMutation.mutate({ id: user.id, aktivni: !user.aktivni });
    };

    const handleToggleNotifikace = (user: Uzivatel) => {
        updateMutation.mutate({
            id: user.id,
            data: { notifikaceEnabled: !(user.notifikaceEnabled ?? true) },
        });
    };

    // Bulk operation handlers
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        setBulkOperationProgress(true);
        try {
            for (const id of selectedIds) {
                await new Promise<void>((resolve, reject) => {
                    deleteMutation.mutate(id as number, {
                        onSuccess: () => resolve(),
                        onError: () => reject(),
                    });
                });
            }
            setSelectedUserIds(new Set());
            setOpenBulkDeleteDialog(false);
        } catch {
            // Error is handled by mutation
        } finally {
            setBulkOperationProgress(false);
        }
    };

    const handleBulkToggleStatus = async () => {
        if (selectedIds.length === 0) return;
        setBulkOperationProgress(true);
        const newStatus = bulkStatusAction === 'activate';
        try {
            for (const id of selectedIds) {
                const user = users?.find(u => u.id === id);
                if (user && user.aktivni !== newStatus) {
                    await new Promise<void>((resolve, reject) => {
                        toggleStatusMutation.mutate({ id: id as number, aktivni: newStatus }, {
                            onSuccess: () => resolve(),
                            onError: () => reject(),
                        });
                    });
                }
            }
            setSelectedUserIds(new Set());
            setOpenBulkStatusDialog(false);
        } catch {
            // Error is handled by mutation
        } finally {
            setBulkOperationProgress(false);
        }
    };

    const handleBulkAssignSpecializace = () => {
        if (selectedIds.length === 0 || !selectedBulkSpecializace) return;
        bulkAssignSpecializaceMutation.mutate(
            { userIds: selectedIds.map(id => id as number), specializaceKod: selectedBulkSpecializace },
            {
                onSuccess: () => {
                    setSelectedUserIds(new Set());
                    setOpenBulkSpecializaceDialog(false);
                    setSelectedBulkSpecializace('');
                },
            }
        );
    };

    const handleBulkSetPin = () => {
        if (selectedIds.length === 0 || !bulkPin || !/^\d{4,6}$/.test(bulkPin)) return;
        bulkSetPinMutation.mutate(
            { userIds: selectedIds.map(id => id as number), pin: bulkPin },
            {
                onSuccess: () => {
                    setSelectedUserIds(new Set());
                    setOpenBulkSetPinDialog(false);
                    setBulkPin('');
                },
            }
        );
    };

    const handleBulkDeletePin = () => {
        if (selectedIds.length === 0) return;
        bulkDeletePinMutation.mutate(
            { userIds: selectedIds.map(id => id as number) },
            {
                onSuccess: () => {
                    setSelectedUserIds(new Set());
                    setOpenBulkDeletePinDialog(false);
                },
            }
        );
    };

    const handleOpenPinDialog = (user: Uzivatel) => {
        setSelectedUser(user);
        setNewPin('');
        setOpenPinDialog(true);
    };

    const handleSetPin = () => {
        if (selectedUser && newPin && /^\d{4,6}$/.test(newPin)) {
            setUserPinMutation.mutate(
                { id: selectedUser.id, pin: newPin },
                {
                    onSuccess: () => {
                        setOpenPinDialog(false);
                        setSelectedUser(null);
                        setNewPin('');
                    },
                }
            );
        }
    };

    const handleDeletePin = () => {
        if (selectedUser) {
            deleteUserPinMutation.mutate(selectedUser.id, {
                onSuccess: () => {
                    setOpenPinDialog(false);
                    setSelectedUser(null);
                },
            });
        }
    };

    const handleOpenTestNotification = (user: Uzivatel) => {
        setSelectedUser(user);
        setTestNotificationOptions({ fcm: true });
        setOpenTestNotificationDialog(true);
    };

    const handleSendTestNotification = () => {
        if (selectedUser) {
            testNotificationMutation.mutate(
                { id: selectedUser.id, data: testNotificationOptions },
                {
                    onSuccess: () => {
                        setOpenTestNotificationDialog(false);
                        setSelectedUser(null);
                    },
                }
            );
        }
    };

    const handleOpenResetTestLimit = (user: Uzivatel) => {
        setSelectedUser(user);
        setOpenResetTestLimitDialog(true);
    };

    const handleResetTestLimit = () => {
        if (selectedUser) {
            resetTestLimitMutation.mutate(selectedUser.id, {
                onSuccess: () => {
                    setOpenResetTestLimitDialog(false);
                    setSelectedUser(null);
                },
            });
        }
    };

    const handleOpenTestHistory = (user: Uzivatel) => {
        setSelectedUser(user);
        setTestHistoryUserId(user.id);
        setOpenTestHistoryDialog(true);
    };

    const handleCloseTestHistory = () => {
        setOpenTestHistoryDialog(false);
        setTestHistoryUserId(null);
        setSelectedUser(null);
    };

    // Filter users based on search query (must be before early return)
    const filteredUsers = React.useMemo(() => {
        if (!users) return [];
        if (!searchQuery.trim()) return users;
        const query = searchQuery.toLowerCase();
        return users.filter(user =>
            user.jmeno?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query) ||
            user.telefon?.toString().includes(query)
        );
    }, [users, searchQuery]);

    const sortedUsers = React.useMemo(() => {
        return [...filteredUsers].sort((a, b) => {
            let aVal: string | number | boolean = '';
            let bVal: string | number | boolean = '';
            switch (orderBy) {
                case 'jmeno': aVal = a.jmeno || ''; bVal = b.jmeno || ''; break;
                case 'telefon': aVal = a.telefon || 0; bVal = b.telefon || 0; break;
                case 'email': aVal = a.email || ''; bVal = b.email || ''; break;
                case 'funkce': {
                    const getFunkce = (u: typeof a) => {
                        const f = Array.isArray(u.funkce) ? u.funkce[0] : u.funkce;
                        return typeof f === 'string' ? f : (f && typeof f === 'object' && 'kod' in f ? String((f as { kod: unknown }).kod) : '');
                    };
                    aVal = getFunkce(a); bVal = getFunkce(b); break;
                }
                case 'specializace': {
                    const getSpec = (u: typeof a) => {
                        const s = Array.isArray(u.specializace) ? u.specializace[0] : u.specializace;
                        return typeof s === 'string' ? s : (s && typeof s === 'object' && 'kod' in s ? String((s as { kod: unknown }).kod) : '');
                    };
                    aVal = getSpec(a); bVal = getSpec(b); break;
                }
                case 'aktivni': aVal = a.aktivni ? 1 : 0; bVal = b.aktivni ? 1 : 0; break;
                default: aVal = a.jmeno || ''; bVal = b.jmeno || '';
            }
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return order === 'asc' ? aVal.localeCompare(bVal, 'cs') : bVal.localeCompare(aVal, 'cs');
            }
            return order === 'asc' ? (aVal < bVal ? -1 : 1) : (aVal > bVal ? -1 : 1);
        });
    }, [filteredUsers, orderBy, order]);

    const handleSort = (column: string) => {
        if (orderBy === column) {
            setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setOrder('asc');
        }
        setPage(0);
    };

    const selectedIds = React.useMemo((): number[] => [...selectedUserIds], [selectedUserIds]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: Uzivatel) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuUser(user);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuUser(null);
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));
        } else {
            setSelectedUserIds(new Set());
        }
    };

    const handleSelectUser = (userId: number) => {
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title="Uživatelé"
                action={
                    isAdmin ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                            Přidat uživatele
                        </Button>
                    ) : undefined
                }
            />

            <Box mb={2}>
                <TextField
                    placeholder="Hledat podle jména, emailu nebo telefonu..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    sx={{ maxWidth: { xs: '100%', sm: 400 } }}
                    autoComplete="off"
                />
            </Box>

            {/* Bulk Actions Toolbar */}
            {isAdmin && selectedIds.length > 0 && (
                <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', bgcolor: 'action.selected' }}>
                    <Chip
                        label={`${selectedIds.length} vybráno`}
                        color="primary"
                    />
                    <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={<ActivateIcon />}
                        onClick={() => {
                            setBulkStatusAction('activate');
                            setOpenBulkStatusDialog(true);
                        }}
                    >
                        Aktivovat
                    </Button>
                    <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<DeactivateIcon />}
                        onClick={() => {
                            setBulkStatusAction('deactivate');
                            setOpenBulkStatusDialog(true);
                        }}
                    >
                        Deaktivovat
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => setOpenBulkDeleteDialog(true)}
                    >
                        Smazat
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => setOpenBulkSpecializaceDialog(true)}
                    >
                        Přidat specializaci
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PinIcon />}
                        onClick={() => setOpenBulkSetPinDialog(true)}
                    >
                        Nastavit PIN
                    </Button>
                    <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<PinIcon />}
                        onClick={() => setOpenBulkDeletePinDialog(true)}
                    >
                        Smazat PIN
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={hromadnyPosudekMutation.isPending ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                        disabled={hromadnyPosudekMutation.isPending}
                        onClick={() => {
                            hromadnyPosudekMutation.mutate(selectedIds, {
                                onSuccess: ({ blob, filename }) => downloadFile(blob, filename),
                            });
                        }}
                    >
                        Vytvořit hromadný dokument
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setSelectedUserIds(new Set())}
                    >
                        Zrušit výběr
                    </Button>
                </Paper>
            )}

            {currentShift && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Dnes slouží <strong>směna {currentShift}</strong> — členové s touto směnou mají automaticky NEJEDU při poplachu
                </Alert>
            )}

            <Box display="flex" gap={1} alignItems="center" mb={2}>
                <Chip
                    label={`Aktivních: ${filteredUsers.filter(u => u.aktivni).length}`}
                    color="success"
                    size="small"
                    variant="outlined"
                />
                <Chip
                    label={`Celkem: ${filteredUsers.length}`}
                    size="small"
                    variant="outlined"
                />
            </Box>

            {isMobile ? (
                /* Mobile: Card List */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                        <Paper
                            key={user.id}
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'action.hover' },
                                ...(selectedUserIds.has(user.id) || menuUser?.id === user.id
                                    ? { backgroundColor: 'action.selected' }
                                    : {}),
                            }}
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                    {isAdmin && (
                                        <Checkbox
                                            checked={selectedUserIds.has(user.id)}
                                            onChange={() => handleSelectUser(user.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            size="small"
                                            sx={{ ml: -0.5 }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                                {user.jmeno}
                                            </Typography>
                                            {(Array.isArray(user.funkce) ? user.funkce : user.funkce ? [user.funkce] : []).map((f: unknown, index: number) => {
                                                const kod = typeof f === 'string' ? f : (f && typeof f === 'object' && 'kod' in f ? String((f as { kod: unknown }).kod) : undefined);
                                                return <Chip key={kod || index} label={kod || '?'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                                            })}
                                            <Chip
                                                label={user.aktivni ? 'Aktivní' : 'Neaktivní'}
                                                color={user.aktivni ? 'success' : 'default'}
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleNotifikace(user); }} sx={{ p: 0.25 }}>
                                                {(user.notifikaceEnabled ?? true)
                                                    ? <NotificationsActiveIcon sx={{ fontSize: 16 }} color="success" />
                                                    : <NotificationsOffIcon sx={{ fontSize: 16 }} color="disabled" />
                                                }
                                            </IconButton>
                                            {(user.validFcmTokenCount ?? 0) > 0 && (
                                                <Chip
                                                    icon={<PhoneAndroidIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={user.validFcmTokenCount}
                                                    size="small"
                                                    variant="outlined"
                                                    color="success"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Box>
                                        {(Array.isArray(user.specializace) ? user.specializace : user.specializace ? [user.specializace] : []).length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                                {(Array.isArray(user.specializace) ? user.specializace : user.specializace ? [user.specializace] : []).map((s: unknown, index: number) => {
                                                    const kod = typeof s === 'string' ? s : (s && typeof s === 'object' && 'kod' in s ? String((s as { kod: unknown }).kod) : undefined);
                                                    return <Chip key={kod || index} label={kod || '?'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                                                })}
                                            </Box>
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {user.telefon} · {user.email || '—'}
                                        </Typography>
                                        {user.hzsSmena && (
                                            <Chip
                                                label={`HZS: ${user.hzsSmena}`}
                                                size="small"
                                                color={currentShift === user.hzsSmena ? 'error' : 'info'}
                                                variant={currentShift === user.hzsSmena ? 'filled' : 'outlined'}
                                                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                {isAdmin && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, user); }}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Paper>
                    ))}
                    {sortedUsers.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Žádní uživatelé</Typography>
                    )}
                    {sortedUsers.length > rowsPerPage && (
                        <TablePagination
                            component="div"
                            count={sortedUsers.length}
                            page={page}
                            onPageChange={(_e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100]}
                            labelRowsPerPage="Řádků:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
                        />
                    )}
                </Box>
            ) : (
            <Paper variant="outlined" sx={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {isAdmin && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectedUserIds.size > 0 && selectedUserIds.size < filteredUsers.length}
                                            checked={filteredUsers.length > 0 && selectedUserIds.size === filteredUsers.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                )}
                                <TableCell sortDirection={orderBy === 'jmeno' ? order : false}>
                                    <TableSortLabel active={orderBy === 'jmeno'} direction={orderBy === 'jmeno' ? order : 'asc'} onClick={() => handleSort('jmeno')}>
                                        Jméno
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'telefon' ? order : false}>
                                    <TableSortLabel active={orderBy === 'telefon'} direction={orderBy === 'telefon' ? order : 'asc'} onClick={() => handleSort('telefon')}>
                                        Telefon
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'email' ? order : false}>
                                    <TableSortLabel active={orderBy === 'email'} direction={orderBy === 'email' ? order : 'asc'} onClick={() => handleSort('email')}>
                                        Email
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'funkce' ? order : false}>
                                    <TableSortLabel active={orderBy === 'funkce'} direction={orderBy === 'funkce' ? order : 'asc'} onClick={() => handleSort('funkce')}>
                                        Funkce
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'specializace' ? order : false}>
                                    <TableSortLabel active={orderBy === 'specializace'} direction={orderBy === 'specializace' ? order : 'asc'} onClick={() => handleSort('specializace')}>
                                        Specializace
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">PIN</TableCell>
                                <TableCell align="center">Notifikace</TableCell>
                                <TableCell align="center">HZS</TableCell>
                                <TableCell align="center" sortDirection={orderBy === 'aktivni' ? order : false}>
                                    <TableSortLabel active={orderBy === 'aktivni'} direction={orderBy === 'aktivni' ? order : 'asc'} onClick={() => handleSort('aktivni')}>
                                        Stav
                                    </TableSortLabel>
                                </TableCell>
                                {isAdmin && <TableCell sx={{ width: 48 }} />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedUsers.length > 0 ? (
                                sortedUsers
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((user) => (
                                    <TableRow
                                        key={user.id}
                                        hover
                                        onClick={() => navigate(`/admin/users/${user.id}`)}
                                        selected={selectedUserIds.has(user.id) || menuUser?.id === user.id}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'action.selected',
                                                '&:hover': {
                                                    backgroundColor: 'action.selected',
                                                },
                                            },
                                        }}
                                    >
                                        {isAdmin && (
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedUserIds.has(user.id)}
                                                    onChange={() => handleSelectUser(user.id)}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'primary.main', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                    {user.jmeno}
                                                </Typography>
                                                {!user.datumNarozeni && (
                                                    <Tooltip title="Chybí datum narození — posudek nebude zašifrován, člen nedostane přílohu" arrow>
                                                        <WarningAmberIcon fontSize="inherit" color="warning" sx={{ fontSize: '0.95rem', flexShrink: 0 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.telefon}</TableCell>
                                        <TableCell>{user.email || ''}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {(Array.isArray(user.funkce) ? user.funkce : user.funkce ? [user.funkce] : []).map((f: unknown, index: number) => {
                                                    const kod = typeof f === 'string' ? f : (f && typeof f === 'object' && 'kod' in f ? String((f as { kod: unknown }).kod) : undefined);
                                                    return <Chip key={kod || index} label={kod || '?'} size="small" variant="outlined" />;
                                                })}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {(Array.isArray(user.specializace) ? user.specializace : user.specializace ? [user.specializace] : []).map((s: unknown, index: number) => {
                                                    const kod = typeof s === 'string' ? s : (s && typeof s === 'object' && 'kod' in s ? String((s as { kod: unknown }).kod) : undefined);
                                                    return <Chip key={kod || index} label={kod || '?'} size="small" variant="outlined" />;
                                                })}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <PinRevealCell userId={user.id} hasPin={!!user.hasPin} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <Tooltip title={(user.notifikaceEnabled ?? true) ? 'Notifikace povoleny — kliknutím zakázat' : 'Notifikace zakázány — kliknutím povolit'}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleNotifikace(user); }}>
                                                        {(user.notifikaceEnabled ?? true)
                                                            ? <NotificationsActiveIcon fontSize="small" color="success" />
                                                            : <NotificationsOffIcon fontSize="small" color="disabled" />
                                                        }
                                                    </IconButton>
                                                </Tooltip>
                                                {(user.validFcmTokenCount ?? 0) > 0 && (
                                                    <Chip
                                                        icon={<PhoneAndroidIcon />}
                                                        label={user.validFcmTokenCount}
                                                        size="small"
                                                        variant="outlined"
                                                        color="success"
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            {user.hzsSmena && (
                                                <Chip
                                                    label={user.hzsSmena}
                                                    size="small"
                                                    color={currentShift === user.hzsSmena ? 'error' : 'info'}
                                                    variant={currentShift === user.hzsSmena ? 'filled' : 'outlined'}
                                                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={user.aktivni ? 'Aktivní' : 'Neaktivní'}
                                                color={user.aktivni ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={isAdmin ? 11 : 9} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">Žádní uživatelé</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {filteredUsers.length > rowsPerPage && (
                    <TablePagination
                        component="div"
                        count={filteredUsers.length}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[25, 50, 100]}
                        labelRowsPerPage="Řádků na stránce:"
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
                    />
                )}
            </Paper>
            )}

            {/* Row Action Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => { handleMenuClose(); if (menuUser) handleOpenEdit(menuUser); }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Upravit</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); if (menuUser) handleToggleStatus(menuUser); }}>
                    <ListItemIcon><ToggleIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{menuUser?.aktivni ? 'Deaktivovat' : 'Aktivovat'}</ListItemText>
                </MenuItem>
                {menuUser?.hasPassword === false && (
                    <MenuItem
                        onClick={() => { handleMenuClose(); if (menuUser) handleSendInvitationFromMenu(menuUser); }}
                        disabled={!menuUser?.email?.trim() || sendInvitationMutation.isPending}
                    >
                        <ListItemIcon><InviteIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>
                            {!menuUser?.email?.trim()
                                ? 'Odeslat pozvánku (doplňte email)'
                                : 'Odeslat pozvánku'}
                        </ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={() => { handleMenuClose(); if (menuUser) handleOpenResetPassword(menuUser); }}>
                    <ListItemIcon><ResetPasswordIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Reset hesla</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); if (menuUser) handleOpenPinDialog(menuUser); }}>
                    <ListItemIcon><PinIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Nastavit PIN</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => { handleMenuClose(); if (menuUser) handleOpenTestNotification(menuUser); }}
                    disabled={!menuUser?.aktivni}
                >
                    <ListItemIcon><TestNotificationIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Test notifikace</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); if (menuUser) handleOpenResetTestLimit(menuUser); }}>
                    <ListItemIcon><ResetTestLimitIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Reset limitu testů</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); if (menuUser) handleOpenTestHistory(menuUser); }}>
                    <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Historie testů</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={() => { handleMenuClose(); if (menuUser) handleOpenDelete(menuUser); }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Smazat</ListItemText>
                </MenuItem>
            </Menu>

            {/* Create/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={(_event, _reason) => {
                    if (invitingLoading) return;
                    handleCloseDialog();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{selectedUser ? 'Upravit uživatele' : 'Nový uživatel'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {formError && (
                            <Alert severity="error" onClose={() => setFormError(null)}>
                                {formError}
                            </Alert>
                        )}
                        {/* Základní údaje */}
                        <Box>
                            <Typography variant="subtitle2" color="text.primary">Základní údaje</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Jméno a kontaktní údaje uživatele.</Typography>
                        </Box>
                        <TextField
                            label="Jméno"
                            fullWidth
                            required
                            value={formData.jmeno}
                            onChange={(e) => setFormData({ ...formData, jmeno: e.target.value })}
                        />
                        <TextField
                            label="Telefon"
                            type="tel"
                            fullWidth
                            required
                            value={formData.telefon || ''}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setFormData({ ...formData, telefon: parseInt(value) || 0 });
                            }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            InputLabelProps={{ shrink: !!formData.telefon }}
                            autoComplete="off"
                        />

                        {/* Funkce a specializace */}
                        <Box>
                            <Typography variant="subtitle2" color="text.primary">Funkce a specializace</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Zařazení uživatele v jednotce.</Typography>
                        </Box>
                        <FormControl fullWidth>
                            <InputLabel>Funkce</InputLabel>
                            <Select
                                multiple
                                value={formData.funkce || []}
                                label="Funkce"
                                onChange={(e) => setFormData({ ...formData, funkce: e.target.value as string[] })}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {(selected as string[]).map((value) => {
                                            const option = FUNKCE_OPTIONS.find(o => o.kod === value);
                                            return <Chip key={value} label={option?.nazev || value} size="small" />;
                                        })}
                                    </Box>
                                )}
                            >
                                {FUNKCE_OPTIONS.map((option) => (
                                    <MenuItem key={option.kod} value={option.kod}>
                                        {option.nazev} ({option.kod})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Specializace</InputLabel>
                            <Select
                                multiple
                                value={formData.specializace || []}
                                label="Specializace"
                                onChange={(e) => setFormData({ ...formData, specializace: e.target.value as string[] })}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {(selected as string[]).map((value) => {
                                            const spec = allSpecializace?.find(s => s.kod === value);
                                            return <Chip key={value} label={spec?.nazev || value} size="small" color="secondary" />;
                                        })}
                                    </Box>
                                )}
                            >
                                {(allSpecializace || []).map((spec) => (
                                    <MenuItem key={spec.kod} value={spec.kod}>
                                        {spec.nazev} ({spec.kod})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Mobilní aplikace - jen v create modu */}
                        {!selectedUser && (
                            <>
                                <Box>
                                    <Typography variant="subtitle2" color="text.primary">Mobilní aplikace</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Přihlášení přes telefonní číslo a PIN kód.</Typography>
                                </Box>
                                <Tooltip title="PIN lze také nastavit hromadně všem uživatelům najednou přes hromadné operace v tabulce." placement="bottom-start">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.generatePin || false}
                                                onChange={(e) => setFormData({ ...formData, generatePin: e.target.checked })}
                                            />
                                        }
                                        label="Vygenerovat PIN (4 číslice)"
                                    />
                                </Tooltip>
                            </>
                        )}

                        {/* Nastavení */}
                        <Box>
                            <Typography variant="subtitle2" color="text.primary">Nastavení</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.aktivni}
                                        onChange={(e) => setFormData({ ...formData, aktivni: e.target.checked })}
                                    />
                                }
                                label="Aktivní"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.notifikaceEnabled ?? true}
                                        onChange={(e) => setFormData({ ...formData, notifikaceEnabled: e.target.checked })}
                                    />
                                }
                                label="Notifikace povoleny"
                            />
                        </Box>

                        {/* Sbalitelná sekce - Webová administrace */}
                        <Divider sx={{ my: 1 }} />
                        <Button
                            onClick={() => setShowAdminAccess(!showAdminAccess)}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.secondary' }}
                            endIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        transform: showAdminAccess ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                    }}
                                />
                            }
                        >
                            Přístup do webové administrace
                        </Button>
                        <Collapse in={showAdminAccess}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.primary">Webová administrace</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Přihlášení přes email a heslo. Vyplňte pouze pokud uživatel potřebuje přístup do webové administrace.
                                    </Typography>
                                </Box>
                                <TextField
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                />
                                {!selectedUser && formData.email && (
                                    <Alert severity="info" sx={{ mt: 0 }}>
                                        Po vytvoření bude uživateli odeslána pozvánka k nastavení hesla (platnost 24 hodin).
                                    </Alert>
                                )}
                                <FormControl fullWidth>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={formData.role || 'USER'}
                                        label="Role"
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        disabled={formData.role === 'SUPER_ADMIN'}
                                    >
                                        <MenuItem value="USER">Uživatel</MenuItem>
                                        <MenuItem value="ADMIN">Administrátor</MenuItem>
                                        {formData.role === 'SUPER_ADMIN' && (
                                            <MenuItem value="SUPER_ADMIN">Super administrátor</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Collapse>

                        {/* Sbalitelná sekce - Kontroly a povinnosti */}
                        {selectedUser && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Button
                                    onClick={() => setShowKontroly(!showKontroly)}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.secondary' }}
                                    endIcon={
                                        <ExpandMoreIcon
                                            sx={{
                                                transform: showKontroly ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s',
                                            }}
                                        />
                                    }
                                >
                                    Kontroly a povinnosti
                                </Button>
                                <Collapse in={showKontroly}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                        {userKontroly?.filter(k => k.relevantni).map((kontrola) => (
                                            <Box
                                                key={kontrola.typKontrolyId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: 'action.hover',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                    <Box>
                                                        <Typography variant="body2">{kontrola.typKontrolyNazev}</Typography>
                                                        <PrevzatoPopisek kontrola={kontrola} />
                                                    </Box>
                                                    {kontrola.status === 'PLATNA' && (
                                                        <Chip color="success" label={`Platná do ${dayjs(kontrola.datumExpirace).format('DD.MM.YYYY')}`} size="small" />
                                                    )}
                                                    {kontrola.status === 'BLIZI_SE_EXPIRACE' && (
                                                        <Chip color="warning" label={`Vyprší ${dayjs(kontrola.datumExpirace).format('DD.MM.YYYY')}`} size="small" />
                                                    )}
                                                    {kontrola.status === 'EXPIROVANA' && (
                                                        <Chip color="error" label={`Expirovaná ${dayjs(kontrola.datumExpirace).format('DD.MM.YYYY')}`} size="small" />
                                                    )}
                                                    {kontrola.status === 'CHYBI' && (
                                                        <Chip variant="outlined" label="Chybí" size="small" />
                                                    )}
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setKontrolaTyp(kontrola);
                                                        setKontrolaDatum(dayjs());
                                                        setKontrolaPoznamka('');
                                                        setKontrolaMetadata({});
                                                        setKontrolaDialogOpen(true);
                                                    }}
                                                >
                                                    Zaznamenat
                                                </Button>
                                            </Box>
                                        ))}
                                        {userKontroly && userKontroly.filter(k => k.relevantni).length === 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                Žádné relevantní kontroly pro tohoto uživatele.
                                            </Typography>
                                        )}
                                    </Box>
                                </Collapse>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                    <Box>
                        {selectedUser?.hasPassword === false && (
                            <Tooltip title={!formData.email ? "Pro odeslání pozvánky doplňte platný email" : ""}>
                                <span>
                                    <Button
                                        startIcon={<InviteIcon />}
                                        variant="outlined"
                                        color="primary"
                                        disabled={!formData.email || invitingLoading}
                                        onClick={() => setShowInviteConfirm(true)}
                                    >
                                        Odeslat pozvánku
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCloseDialog} disabled={invitingLoading}>Zrušit</Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={createMutation.isPending || updateMutation.isPending || invitingLoading || !formData.jmeno?.trim() || !formData.telefon}
                        >
                            {selectedUser ? 'Uložit' : 'Vytvořit'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Kontrola zaznamenání Dialog */}
            <Dialog
                open={kontrolaDialogOpen}
                onClose={() => setKontrolaDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {kontrolaTyp?.typKontrolyNazev} — {selectedUser?.jmeno}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <DatePicker
                            label="Datum splnění"
                            value={kontrolaDatum}
                            onChange={setKontrolaDatum}
                            format="DD.MM.YYYY"
                            slotProps={{ textField: { fullWidth: true, size: 'small', margin: 'dense' } }}
                        />
                        <TextField
                            label="Poznámka"
                            value={kontrolaPoznamka}
                            onChange={(e) => setKontrolaPoznamka(e.target.value)}
                            fullWidth
                            margin="dense"
                            size="small"
                        />
                        {kontrolaTyp?.metadataSchema && (() => {
                            try {
                                const schema: MetadataFieldSchema[] = JSON.parse(kontrolaTyp.metadataSchema);
                                return schema.map((field) => {
                                    if (field.type === 'technika') {
                                        return (
                                            <FormControl key={field.key} fullWidth margin="dense" size="small">
                                                <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
                                                <Select
                                                    value={kontrolaMetadata[field.key] || ''}
                                                    label={`${field.label}${field.required ? ' *' : ''}`}
                                                    onChange={(e) => setKontrolaMetadata({ ...kontrolaMetadata, [field.key]: e.target.value })}
                                                >
                                                    <MenuItem value="">— Nevybráno —</MenuItem>
                                                    {aktivniTechnika?.map((t) => (
                                                        <MenuItem key={t.id} value={t.id}>
                                                            {t.volaciZnak} - {t.nazev}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        );
                                    }
                                    return (
                                        <TextField
                                            key={field.key}
                                            label={field.label}
                                            type={field.type === 'number' ? 'number' : 'text'}
                                            value={kontrolaMetadata[field.key] || ''}
                                            onChange={(e) => setKontrolaMetadata({
                                                ...kontrolaMetadata,
                                                [field.key]: field.type === 'number'
                                                    ? (e.target.value === '' ? '' : Number(e.target.value))
                                                    : e.target.value
                                            })}
                                            required={field.required}
                                            fullWidth
                                            margin="dense"
                                            size="small"
                                        />
                                    );
                                });
                            } catch {
                                return null;
                            }
                        })()}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKontrolaDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        disabled={!kontrolaDatum || createKontrolaMutation.isPending}
                        onClick={() => {
                            if (!kontrolaDatum || !kontrolaTyp || !selectedUser) return;
                            const metadataJson = Object.keys(kontrolaMetadata).some(k => kontrolaMetadata[k] !== '' && kontrolaMetadata[k] !== undefined)
                                ? JSON.stringify(kontrolaMetadata)
                                : undefined;
                            createKontrolaMutation.mutate({
                                uzivatelId: selectedUser.id,
                                typKontrolyId: kontrolaTyp.typKontrolyId,
                                datumSplneni: kontrolaDatum.format('YYYY-MM-DD'),
                                poznamka: kontrolaPoznamka || undefined,
                                metadata: metadataJson,
                            }, {
                                onSuccess: () => setKontrolaDialogOpen(false),
                            });
                        }}
                    >
                        {createKontrolaMutation.isPending ? <CircularProgress size={20} /> : 'Zaznamenat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat uživatele?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat uživatele <strong>{selectedUser?.jmeno}</strong>?
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Účet se odstraní ze všech seznamů a přihlásit se s ním už nepůjde.
                        Zmizí e-mail, telefon, heslo i PIN. Záznamy o tom, ke kterým výjezdům
                        člen jel, zůstanou zachované včetně jména. Tuto akci nelze vrátit.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
                <DialogTitle>Reset hesla</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Resetujte heslo pro uživatele <strong>{selectedUser?.jmeno}</strong>
                    </Typography>
                    {selectedUser?.email && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={sendResetEmail}
                                    onChange={(e) => setSendResetEmail(e.target.checked)}
                                />
                            }
                            label="Odeslat odkaz na reset hesla emailem"
                            sx={{ mb: 2 }}
                        />
                    )}
                    {!sendResetEmail && (
                        <TextField
                            label="Nové heslo"
                            type="password"
                            fullWidth
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    )}
                    {sendResetEmail && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Uživatel obdrží email s odkazem pro nastavení nového hesla.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPasswordDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleResetPassword}
                        disabled={(!sendResetEmail && !newPassword) || resetPasswordMutation.isPending}
                    >
                        {sendResetEmail ? 'Odeslat email' : 'Resetovat heslo'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Invitation Confirmation Dialog */}
            <Dialog
                open={showInviteConfirm}
                onClose={() => !invitingLoading && setShowInviteConfirm(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Odeslat pozvánku?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Uživateli <strong>{selectedUser?.jmeno}</strong> bude na adresu{' '}
                        <strong>{formData.email}</strong> odeslán email s odkazem pro nastavení hesla.
                        Odkaz je platný 24 hodin.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowInviteConfirm(false)} disabled={invitingLoading}>
                        Zrušit
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendInvitation}
                        disabled={invitingLoading}
                    >
                        {invitingLoading ? 'Odesílám...' : 'Odeslat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* PIN Dialog */}
            <Dialog open={openPinDialog} onClose={() => setOpenPinDialog(false)}>
                <DialogTitle>Nastavit PIN</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Nastavte PIN pro uživatele <strong>{selectedUser?.jmeno}</strong>
                    </Typography>
                    <TextField
                        label="PIN (4-6 číslic)"
                        fullWidth
                        value={newPin}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setNewPin(value);
                        }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                        helperText="PIN musí obsahovat 4-6 číslic"
                        error={newPin.length > 0 && (newPin.length < 4 || newPin.length > 6)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleDeletePin}
                        disabled={deleteUserPinMutation.isPending}
                    >
                        Smazat PIN
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => setOpenPinDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSetPin}
                        disabled={!newPin || newPin.length < 4 || newPin.length > 6 || setUserPinMutation.isPending}
                    >
                        Nastavit PIN
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generated PIN Dialog */}
            <Dialog open={openGeneratedPinDialog} onClose={() => setOpenGeneratedPinDialog(false)}>
                <DialogTitle>PIN byl vygenerován</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Uživateli byl vygenerován následující PIN:
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 3,
                            bgcolor: 'action.hover',
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="h3" sx={{ fontFamily: 'monospace', letterSpacing: 4 }}>
                            {generatedPin}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Poznamenejte si tento PIN a předejte ho uživateli. PIN slouží k rychlému přihlášení v mobilní aplikaci.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setOpenGeneratedPinDialog(false);
                            setGeneratedPin(null);
                        }}
                    >
                        Rozumím
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Delete Confirmation Dialog */}
            <Dialog open={openBulkDeleteDialog} onClose={() => setOpenBulkDeleteDialog(false)}>
                <DialogTitle>Hromadné smazání uživatelů</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Tato operace je nevratná!
                    </Alert>
                    <Typography>
                        Opravdu chcete smazat <strong>{selectedIds.length}</strong> vybraných uživatelů?
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Účty se odstraní ze všech seznamů a přihlásit se s nimi už nepůjde. Záznamy o jejich výjezdech zůstanou zachované.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBulkDeleteDialog(false)} disabled={bulkOperationProgress}>
                        Zrušit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleBulkDelete}
                        disabled={bulkOperationProgress}
                        startIcon={bulkOperationProgress ? <CircularProgress size={16} /> : <DeleteIcon />}
                    >
                        {bulkOperationProgress ? 'Mažu...' : `Smazat ${selectedIds.length} uživatelů`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Status Change Dialog */}
            <Dialog open={openBulkStatusDialog} onClose={() => setOpenBulkStatusDialog(false)}>
                <DialogTitle>
                    Hromadná {bulkStatusAction === 'activate' ? 'aktivace' : 'deaktivace'} uživatelů
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete {bulkStatusAction === 'activate' ? 'aktivovat' : 'deaktivovat'}{' '}
                        <strong>{selectedIds.length}</strong> vybraných uživatelů?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBulkStatusDialog(false)} disabled={bulkOperationProgress}>
                        Zrušit
                    </Button>
                    <Button
                        variant="contained"
                        color={bulkStatusAction === 'activate' ? 'success' : 'warning'}
                        onClick={handleBulkToggleStatus}
                        disabled={bulkOperationProgress}
                        startIcon={bulkOperationProgress ? <CircularProgress size={16} /> : (bulkStatusAction === 'activate' ? <ActivateIcon /> : <DeactivateIcon />)}
                    >
                        {bulkOperationProgress
                            ? 'Zpracovávám...'
                            : `${bulkStatusAction === 'activate' ? 'Aktivovat' : 'Deaktivovat'} ${selectedIds.length} uživatelů`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Assign Specializace Dialog */}
            <Dialog open={openBulkSpecializaceDialog} onClose={() => setOpenBulkSpecializaceDialog(false)}>
                <DialogTitle>Hromadné přiřazení specializace</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Přiřadit specializaci <strong>{selectedIds.length}</strong> vybraným uživatelům:
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Specializace</InputLabel>
                        <Select
                            value={selectedBulkSpecializace}
                            label="Specializace"
                            onChange={(e) => setSelectedBulkSpecializace(e.target.value)}
                        >
                            {(allSpecializace || []).map((spec) => (
                                <MenuItem key={spec.kod} value={spec.kod}>
                                    {spec.nazev} ({spec.kod})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBulkSpecializaceDialog(false)} disabled={bulkAssignSpecializaceMutation.isPending}>
                        Zrušit
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleBulkAssignSpecializace}
                        disabled={!selectedBulkSpecializace || bulkAssignSpecializaceMutation.isPending}
                        startIcon={bulkAssignSpecializaceMutation.isPending ? <CircularProgress size={16} /> : <AssignmentIcon />}
                    >
                        {bulkAssignSpecializaceMutation.isPending ? 'Přiřazuji...' : 'Přiřadit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Set PIN Dialog */}
            <Dialog open={openBulkSetPinDialog} onClose={() => setOpenBulkSetPinDialog(false)}>
                <DialogTitle>Hromadné nastavení PINu</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Nastavit PIN pro <strong>{selectedIds.length}</strong> vybraných uživatelů:
                    </Typography>
                    <TextField
                        label="PIN (4-6 číslic)"
                        fullWidth
                        value={bulkPin}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setBulkPin(value);
                        }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                        helperText="PIN musí obsahovat 4-6 číslic. Bude nastaven stejný pro všechny vybrané uživatele."
                        error={bulkPin.length > 0 && (bulkPin.length < 4 || bulkPin.length > 6)}
                        sx={{ mt: 1 }}
                    />
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Nastavení PINu invaliduje přihlašovací tokeny dotčených uživatelů.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBulkSetPinDialog(false)} disabled={bulkSetPinMutation.isPending}>
                        Zrušit
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleBulkSetPin}
                        disabled={!bulkPin || bulkPin.length < 4 || bulkPin.length > 6 || bulkSetPinMutation.isPending}
                        startIcon={bulkSetPinMutation.isPending ? <CircularProgress size={16} /> : <PinIcon />}
                    >
                        {bulkSetPinMutation.isPending ? 'Nastavuji...' : 'Nastavit PIN'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Delete PIN Dialog */}
            <Dialog open={openBulkDeletePinDialog} onClose={() => setOpenBulkDeletePinDialog(false)}>
                <DialogTitle>Hromadné smazání PINu</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Uživatelé bez PINu se nebudou moci přihlásit přes mobilní aplikaci pomocí telefonu a PINu.
                    </Alert>
                    <Typography>
                        Opravdu chcete smazat PIN pro <strong>{selectedIds.length}</strong> vybraných uživatelů?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBulkDeletePinDialog(false)} disabled={bulkDeletePinMutation.isPending}>
                        Zrušit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleBulkDeletePin}
                        disabled={bulkDeletePinMutation.isPending}
                        startIcon={bulkDeletePinMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
                    >
                        {bulkDeletePinMutation.isPending ? 'Mažu...' : `Smazat PIN (${selectedIds.length})`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test Notification Dialog */}
            <Dialog open={openTestNotificationDialog} onClose={() => setOpenTestNotificationDialog(false)}>
                <DialogTitle>Test notifikace</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        Odešle testovací notifikaci uživateli <strong>{selectedUser?.jmeno}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Jedná se o běžnou notifikaci, ne o kritický poplach. Slouží pouze k ověření, že notifikace dorazí do mobilní aplikace.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={testNotificationOptions.fcm}
                                    onChange={(e) => setTestNotificationOptions(prev => ({ ...prev, fcm: e.target.checked }))}
                                />
                            }
                            label={`FCM (${selectedUser?.validFcmTokenCount || 0} zařízení)`}
                            disabled={!selectedUser?.validFcmTokenCount}
                        />
                    </Box>
                    {!testNotificationOptions.fcm && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Vyberte alespoň jeden kanál
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTestNotificationDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSendTestNotification}
                        disabled={
                            !testNotificationOptions.fcm ||
                            testNotificationMutation.isPending
                        }
                    >
                        Odeslat test
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Test Limit Dialog */}
            <Dialog open={openResetTestLimitDialog} onClose={() => setOpenResetTestLimitDialog(false)}>
                <DialogTitle>Reset limitu testovacích poplachů</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete resetovat denní limit testovacích poplachů pro uživatele <strong>{selectedUser?.jmeno}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Toto obnoví limit na 5 testů za 24 hodin.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenResetTestLimitDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleResetTestLimit}
                        disabled={resetTestLimitMutation.isPending}
                    >
                        Resetovat limit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test History Dialog */}
            <Dialog open={openTestHistoryDialog} onClose={handleCloseTestHistory} maxWidth="md" fullWidth>
                <DialogTitle>Historie testovacích poplachů - {selectedUser?.jmeno}</DialogTitle>
                <DialogContent>
                    {testHistoryLoading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : testHistoryData && testHistoryData.length > 0 ? (
                        <Box sx={{ mt: 1 }}>
                            <DataGrid
                                rows={testHistoryData}
                                columns={[
                                    { field: 'id', headerName: 'ID', width: 70 },
                                    {
                                        field: 'stav',
                                        headerName: 'Stav',
                                        width: 120,
                                        renderCell: (params) => {
                                            const stavColors: Record<string, 'success' | 'warning' | 'default' | 'error' | 'info'> = {
                                                ODESLANO: 'info',
                                                CEKAJICI: 'warning',
                                                ZRUSENO: 'default',
                                                DEAKTIVOVANO: 'default',
                                                ZMESKANO: 'error',
                                                DOKONCENO: 'success',
                                            };
                                            const stavLabels: Record<string, string> = {
                                                ODESLANO: 'Odesláno',
                                                CEKAJICI: 'Čekající',
                                                ZRUSENO: 'Zrušeno',
                                                DEAKTIVOVANO: 'Deaktivováno',
                                                ZMESKANO: 'Zmeškáno',
                                                DOKONCENO: 'Dokončeno',
                                            };
                                            return (
                                                <Chip
                                                    label={stavLabels[params.value] || params.value}
                                                    color={stavColors[params.value] || 'default'}
                                                    size="small"
                                                />
                                            );
                                        },
                                    },
                                    {
                                        field: 'createdAt',
                                        headerName: 'Vytvořeno',
                                        width: 180,
                                        valueFormatter: (value: string) =>
                                            value ? new Date(value).toLocaleString('cs-CZ') : '-',
                                    },
                                    {
                                        field: 'scheduledTime',
                                        headerName: 'Naplánováno na',
                                        width: 180,
                                        valueFormatter: (value: string) =>
                                            value ? new Date(value).toLocaleString('cs-CZ') : '-',
                                    },
                                    {
                                        field: 'executedAt',
                                        headerName: 'Provedeno',
                                        width: 180,
                                        valueFormatter: (value: string | null | undefined) =>
                                            value ? new Date(value).toLocaleString('cs-CZ') : '-',
                                    },
                                    {
                                        field: 'odpoved',
                                        headerName: 'Odpověď',
                                        width: 100,
                                        renderCell: (params) => {
                                            if (!params.value) return '-';
                                            return (
                                                <Chip
                                                    label={params.value}
                                                    color={params.value === 'JEDU' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            );
                                        },
                                    },
                                    {
                                        field: 'odpovedCas',
                                        headerName: 'Čas odpovědi',
                                        width: 180,
                                        valueFormatter: (value: string | null | undefined) =>
                                            value ? new Date(value).toLocaleString('cs-CZ') : '-',
                                    },
                                ]}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 10 } },
                                }}
                                pageSizeOptions={[5, 10, 25]}
                                autoHeight
                                disableRowSelectionOnClick
                                localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
                            />
                        </Box>
                    ) : (
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            Žádné testovací poplachy
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTestHistory}>Zavřít</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UsersPage;
